'use client';

import { AlertTriangle, Loader2 } from 'lucide-react';
import { useOptionalPrintingState } from '@/features/printing/presentation/providers/printing-provider';
import { cn } from '@/shared/utils/utils';

/**
 * Chip para la barra de estado de Take Order que indica cuándo hay impresiones
 * en vuelo o si hubo algún error reciente en segundo plano.
 */
export function PrintingInProgressBadge() {
  const printingState = useOptionalPrintingState();

  if (!printingState) return null;

  const { isPrinting, channelStates } = printingState;

  // Detectar si hay algún canal activo
  const loadingChannel = (Object.keys(channelStates) as (keyof typeof channelStates)[]).find(
    ch => channelStates[ch].status === 'loading'
  );

  // Detectar errores recientes
  const errorChannel = (Object.keys(channelStates) as (keyof typeof channelStates)[]).find(
    ch => channelStates[ch].status === 'error'
  );

  if (isPrinting || loadingChannel) {
    const label =
      loadingChannel === 'cocina'
        ? 'Imprimiendo cocina…'
        : loadingChannel === 'caja'
          ? 'Imprimiendo ticket…'
          : loadingChannel === 'fiscal'
            ? 'Emitiendo factura…'
            : 'Imprimiendo…';

    return (
      <div
        className={cn(
          'flex h-7 items-center gap-1.5 rounded-full px-2.5 text-xs font-semibold border animate-pulse',
          'border-sky-500/40 bg-sky-500/10 text-sky-600 dark:text-sky-400'
        )}
        title="Impresión en curso en segundo plano"
      >
        <Loader2 className="size-3.5 animate-spin" />
        <span className="max-w-36 truncate">{label}</span>
      </div>
    );
  }

  if (errorChannel) {
    const errorMsg = channelStates[errorChannel].message ?? 'Error';
    return (
      <div
        className={cn(
          'flex h-7 items-center gap-1.5 rounded-full px-2.5 text-xs font-semibold border',
          'border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400'
        )}
        title={`Error en ${errorChannel}: ${errorMsg}`}
      >
        <AlertTriangle className="size-3.5" />
        <span className="max-w-32 truncate">Error ({errorChannel})</span>
      </div>
    );
  }

  return null;
}
